import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listPublicMoleculeDrawings, type PublicMoleculeDrawingSummary } from '@peruselab/api-client/moleculeDrawings';
import { searchMoleculesByText, type ContainerResult } from '@peruselab/api-client/moleculeSearch';
import { listPublicReactions, type PublicReactionSummary } from '@peruselab/api-client/reactions';
import { Icon } from '../../components/Icon';
import { PublicDrawingCard } from './PublicDrawingCard';
import { PublicReactionCard } from './PublicReactionCard';
import { PublicLayout } from './PublicLayout';
import { usePublicAccess } from './publicAccess';

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 300;

type Browse =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly PublicMoleculeDrawingSummary[]; readonly hasMore: boolean }
  | { readonly status: 'error' };

type BrowseReactions =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly PublicReactionSummary[]; readonly hasMore: boolean }
  | { readonly status: 'error' };

type Search =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly query: string;
      readonly drawings: readonly ContainerResult[];
      readonly reactions: readonly ContainerResult[];
    }
  | { readonly status: 'error' };

/**
 * #885: the public catalog's home -- search public drawings by name, formula or SMILES, or
 * browse the recently updated ones. Open to signed-out visitors. #1136: public reactions too,
 * beside the drawings, each opening its page at /r/<id>.
 */
export function PublicHome() {
  const { t } = useTranslation();
  const { signedIn } = usePublicAccess();
  const [browse, setBrowse] = useState<Browse>({ status: 'loading' });
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState<Search>({ status: 'idle' });
  const [reactions, setReactions] = useState<BrowseReactions>({ status: 'loading' });
  const [reactionPage, setReactionPage] = useState(0);

  useEffect(() => {
    let cancelled = false;
    listPublicReactions(reactionPage, PAGE_SIZE)
      .then((result) => {
        if (cancelled) return;
        setReactions((current) => ({
          status: 'ready',
          items:
            reactionPage === 0 || current.status !== 'ready' ? result.content : [...current.items, ...result.content],
          hasMore: reactionPage + 1 < result.totalPages
        }));
      })
      .catch(() => {
        if (!cancelled) setReactions({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [reactionPage]);

  useEffect(() => {
    let cancelled = false;
    listPublicMoleculeDrawings(page, PAGE_SIZE)
      .then((result) => {
        if (cancelled) return;
        setBrowse((current) => ({
          status: 'ready',
          items: page === 0 || current.status !== 'ready' ? result.content : [...current.items, ...result.content],
          hasMore: page + 1 < result.totalPages
        }));
      })
      .catch(() => {
        if (!cancelled) setBrowse({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [page]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearch({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setSearch({ status: 'loading' });
    const timer = globalThis.setTimeout(() => {
      searchMoleculesByText(trimmed)
        .then((results) => {
          if (cancelled) return;
          // Signed in, search also returns your own private drawings -- this page stays public.
          const others = (found: readonly ContainerResult[]) =>
            signedIn ? found.filter((result) => result.owned === false) : found;
          const drawings = others(results.drawings);
          // #1136: reactions found through their molecules and by their own words, once each.
          const reactions = others([...results.reactions, ...(results.reactionsByText ?? [])]).filter(
            (reaction, index, all) => all.findIndex((other) => other.documentId === reaction.documentId) === index
          );
          setSearch({ status: 'ready', query: trimmed, drawings, reactions });
        })
        .catch(() => {
          if (!cancelled) setSearch({ status: 'error' });
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      globalThis.clearTimeout(timer);
    };
  }, [query, signedIn]);

  return (
    <PublicLayout>
      <section className="public-hero" aria-labelledby="public-home-heading">
        <h1 id="public-home-heading">{t('publicPages.homeHeading')}</h1>
        <p>{t('publicPages.homeIntro')}</p>
        <label className="public-search">
          <Icon name="search" />
          <span className="visually-hidden">{t('publicPages.searchLabel')}</span>
          <input
            type="search"
            value={query}
            placeholder={t('publicPages.searchPlaceholder')}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
      </section>

      {search.status === 'idle' ? (
        <section aria-labelledby="public-recent-heading">
          <h2 id="public-recent-heading">{t('publicPages.recentHeading')}</h2>
          {browse.status === 'loading' ? <p role="status">{t('common.loading')}</p> : null}
          {browse.status === 'error' ? (
            <p className="import-error" role="alert">
              {t('publicPages.loadError')}
            </p>
          ) : null}
          {browse.status === 'ready' && browse.items.length === 0 ? <p>{t('publicPages.noneYet')}</p> : null}
          {browse.status === 'ready' && browse.items.length > 0 ? (
            <>
              <ul className="public-drawing-grid">
                {browse.items.map((item) => (
                  <PublicDrawingCard
                    key={item.id}
                    drawingId={item.id}
                    title={item.title}
                    updatedAt={item.updatedAt}
                    authorName={item.authorName}
                  />
                ))}
              </ul>
              {browse.hasMore ? (
                <button
                  type="button"
                  className="public-load-more icon-button"
                  onClick={() => setPage((current) => current + 1)}
                  aria-label={t('publicPages.loadMore')}
                  title={t('publicPages.loadMore')}
                >
                  <Icon name="expandMore" />
                </button>
              ) : null}
            </>
          ) : null}
        </section>
      ) : null}

      {search.status === 'idle' ? (
        <section aria-labelledby="public-reactions-heading">
          <h2 id="public-reactions-heading">{t('publicPages.recentReactionsHeading')}</h2>
          {reactions.status === 'loading' ? <p role="status">{t('common.loading')}</p> : null}
          {reactions.status === 'error' ? (
            <p className="import-error" role="alert">
              {t('publicPages.loadError')}
            </p>
          ) : null}
          {reactions.status === 'ready' && reactions.items.length === 0 ? (
            <p>{t('publicPages.noReactionsYet')}</p>
          ) : null}
          {reactions.status === 'ready' && reactions.items.length > 0 ? (
            <>
              <ul className="public-drawing-grid">
                {reactions.items.map((item) => (
                  <PublicReactionCard
                    key={item.id}
                    reactionId={item.id}
                    label={item.label}
                    authorName={item.authorName}
                  />
                ))}
              </ul>
              {reactions.hasMore ? (
                <button
                  type="button"
                  className="public-load-more icon-button"
                  onClick={() => setReactionPage((current) => current + 1)}
                  aria-label={t('publicPages.loadMoreReactions')}
                  title={t('publicPages.loadMoreReactions')}
                >
                  <Icon name="expandMore" />
                </button>
              ) : null}
            </>
          ) : null}
        </section>
      ) : (
        <section aria-labelledby="public-results-heading">
          <h2 id="public-results-heading">{t('publicPages.resultsHeading', { query: query.trim() })}</h2>
          {search.status === 'loading' ? <p role="status">{t('common.loading')}</p> : null}
          {search.status === 'error' ? (
            <p className="import-error" role="alert">
              {t('publicPages.loadError')}
            </p>
          ) : null}
          {search.status === 'ready' && search.drawings.length === 0 && search.reactions.length === 0 ? (
            <p>{t('publicPages.noResults', { query: search.query })}</p>
          ) : null}
          {search.status === 'ready' && (search.drawings.length > 0 || search.reactions.length > 0) ? (
            <ul className="public-drawing-grid">
              {search.drawings.map((drawing) => (
                <PublicDrawingCard
                  key={drawing.documentId}
                  drawingId={drawing.documentId}
                  title={drawing.title}
                  updatedAt={drawing.updatedAt}
                />
              ))}
              {search.reactions.map((reaction) => (
                <PublicReactionCard key={reaction.documentId} reactionId={reaction.documentId} label={reaction.title} />
              ))}
            </ul>
          ) : null}
        </section>
      )}
    </PublicLayout>
  );
}
